// Desktop notifications for incoming chat messages.
//
// All contact with the Notification API happens here, and every call is
// guarded: the API is unavailable in an insecure context (the whole feature
// needs the site on HTTPS), absent in older Safari, and can throw rather than
// return on some builds. Nothing in here may throw into a caller - a failure
// just means no notification, and the app behaves exactly as it did before.
//
// Click behaviour is deliberately NOT here, and never becomes optional:
// clicking a toast is inert, so it can never be a shortcut that puts a
// conversation on screen.
//
// Message content is different - it is off by default and opt-in, through the
// 'notifications.preview' preference. Callers decide; this module only renders
// what it is handed. Passing no body reproduces the original behaviour
// exactly, which is what an unset preference gets.

export const AUTO_CLOSE_MS = 5000;

const TITLE_PREFIX = 'New message from ';
const TAG_PREFIX = 'teamx-msg-';

// A toast body is truncated here as well as by the OS, because the OS limit
// governs what is displayed while this governs what is written into the
// Windows notification history, where it stays. Announcements are cut harder:
// they are broadcast prose rather than a line of chat, so the opening clause
// is all a toast can usefully carry. Post.Message is nvarchar(500), so both
// caps do real work.
export const MESSAGE_PREVIEW_MAX = 140;
export const ANNOUNCEMENT_PREVIEW_MAX = 90;

// Collapses whitespace and trims to `max`, ellipsis included in the budget.
// Returns undefined for anything with no text in it, so a caller can hand the
// result straight to the Notification options and get no body key at all
// rather than an empty one.
export const previewText = (text, max) => {
    if (typeof text !== 'string') {
        return undefined;
    }
    const collapsed = text.replace(/\s+/g, ' ').trim();
    if (collapsed === '') {
        return undefined;
    }
    if (collapsed.length <= max) {
        return collapsed;
    }
    return collapsed.slice(0, Math.max(0, max - 1)).trim() + '…';
};

export const isSupported = () => {
    try {
        return typeof window !== 'undefined' && 'Notification' in window && !!window.Notification;
    } catch (e) {
        return false;
    }
};

// 'granted' | 'denied' | 'default' | 'unsupported'
export const getPermission = () => {
    if (!isSupported()) {
        return 'unsupported';
    }
    try {
        return window.Notification.permission;
    } catch (e) {
        return 'unsupported';
    }
};

export const canNotify = () => getPermission() === 'granted';

// Must be called from a user gesture - Firefox 72+ refuses the prompt
// otherwise, and silently, so an automatic on-load request looks like it
// worked and never shows anything.
//
// Handles both spec'd shapes: modern browsers return a promise, older Safari
// takes a callback and returns undefined. Whichever answers first wins.
export const requestPermission = () => {
    if (!isSupported()) {
        return Promise.resolve('unsupported');
    }
    try {
        return new Promise(resolve => {
            let settled = false;
            const done = (result) => {
                if (settled) {
                    return;
                }
                settled = true;
                resolve(result || getPermission());
            };
            // The try has to be in here, not around the Promise: a throw from
            // the executor rejects the promise instead of reaching an outer
            // catch, and this function must never reject.
            try {
                const returned = window.Notification.requestPermission(done);
                if (returned && typeof returned.then === 'function') {
                    returned.then(done, () => done('denied'));
                }
            } catch (e) {
                done('denied');
            }
        });
    } catch (e) {
        return Promise.resolve('denied');
    }
};

// True when the user is looking at neither TeamX window.
//
// Chat lives in a separate browser window, so the main document having no
// focus is not enough - somebody reading their chat would still get notified.
// The chat handle comes from <NewWindow>'s onOpen rather than from
// window.open('', 'chatBoxWindow'), because that call CREATES a window when
// none by that name exists and would spawn a blank one on every message.
export const noWindowFocused = (chatWindow) => {
    try {
        if (document.hasFocus()) {
            return false;
        }
    } catch (e) {
        // No way to tell; fall through and let the chat window decide.
    }

    try {
        if (chatWindow && !chatWindow.closed && chatWindow.document && chatWindow.document.hasFocus()) {
            return false;
        }
    } catch (e) {
        // A closing or cross-origin handle can throw on access. Treat it as
        // not focused rather than losing the notification.
    }

    return true;
};

// `body` is the caller's already-decided preview, or nothing. The tag is
// unchanged: repeat messages from one sender still replace rather than stack,
// which with a preview means the toast always carries their newest line.
export const showMessageNotification = ({ senderName, senderId, body }) => {
    if (!canNotify()) {
        return null;
    }

    try {
        const options = {
            tag: TAG_PREFIX + senderId,
            icon: 'teamX.ico',
            lang: 'en',
            dir: 'ltr',
        };
        // Set only when there is something to show, so the no-preview case
        // builds the identical options object it always did.
        const preview = previewText(body, MESSAGE_PREVIEW_MAX);
        if (preview) {
            options.body = preview;
        }
        const note = new window.Notification(TITLE_PREFIX + senderName, options);

        // No onclick handler, on purpose: clicking a notification dismisses
        // it and does nothing else. It never focuses a window and never opens
        // a conversation, so a toast cannot put a message on screen.

        // Windows hides the toast on its own and moves it to the Action
        // Center; this closes it properly so a stale one cannot be clicked
        // minutes later.
        setTimeout(() => {
            try {
                note.close();
            } catch (e) {
                // Already gone.
            }
        }, AUTO_CLOSE_MS);

        return note;
    } catch (e) {
        return null;
    }
};

const ANNOUNCEMENT_TAG = 'teamx-announcement';

// Same rules as a message toast: a title, a body only when the caller has
// decided one is wanted, and never a click handler.
export const showAnnouncementNotification = ({ title, body }) => {
    if (!canNotify()) {
        return null;
    }

    try {
        const options = {
            tag: ANNOUNCEMENT_TAG,
            icon: 'teamX.ico',
            lang: 'en',
            dir: 'ltr',
        };
        const preview = previewText(body, ANNOUNCEMENT_PREVIEW_MAX);
        if (preview) {
            options.body = preview;
        }
        const note = new window.Notification(title || 'New announcement', options);

        // No onclick, on purpose: see the note at the top of this file.

        setTimeout(() => {
            try {
                note.close();
            } catch (e) {
                // Already gone.
            }
        }, AUTO_CLOSE_MS);

        return note;
    } catch (e) {
        return null;
    }
};

export default {
    AUTO_CLOSE_MS,
    MESSAGE_PREVIEW_MAX,
    ANNOUNCEMENT_PREVIEW_MAX,
    previewText,
    isSupported,
    getPermission,
    canNotify,
    requestPermission,
    noWindowFocused,
    showMessageNotification,
    showAnnouncementNotification,
};



// WEBPACK FOOTER //
// ./src/utils/desktopNotifications.js